/**
 * Admin API service - handles super admin platform management API calls
 */

import { apiClient } from './client';

// =============================================================================
// Types
// =============================================================================

export interface PlatformStats {
  totalUsers: number;
  activeUsers30d: number;
  totalOrganizations: number;
  activeOrganizations: number;
  totalProjects: number;
  totalTasks: number;
  totalStorageBytes: number;
  organizationsByTier: Record<string, number>;
}

export interface AdminUserOrgMembership {
  organizationId: string;
  organizationName: string;
  role: string;
  joinedAt: string;
}

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  locale: string;
  theme: string;
  timezone: string;
  isEmailVerified: boolean;
  isActive: boolean;
  isSuperAdmin: boolean;
  lastLoginAt?: string;
  lastActiveAt?: string;
  failedLoginAttempts: number;
  lockedUntil?: string;
  organizations: AdminUserOrgMembership[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserUpdate {
  isActive?: boolean;
  isSuperAdmin?: boolean;
  isEmailVerified?: boolean;
}

export interface AdminUserListParams {
  search?: string;
  isActive?: boolean;
  isSuperAdmin?: boolean;
  organizationId?: string;
  page?: number;
  pageSize?: number;
}

export interface AdminOrganization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  tier: string;
  effectiveTier: string;
  tierExpiresAt?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionPlanName?: string;
  memberCount: number;
  projectCount: number;
  storageUsedBytes: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOrganizationUpdate {
  tier?: 'free' | 'pro' | 'business' | 'enterprise';
  tierExpiresAt?: string;
  isDeleted?: boolean;
}

export interface AdminOrgListParams {
  search?: string;
  tier?: string;
  includeDeleted?: boolean;
  page?: number;
  pageSize?: number;
}

export interface OrganizationUsageSummary {
  organizationId: string;
  organizationName: string;
  tier: string;
  transcriptionMinutesUsed: number;
  transcriptionMinutesLimit: number;
  extractionCreditsUsed: number;
  extractionCreditsLimit: number;
  storageUsedBytes: number;
  storageLimitBytes: number;
}

export interface PlatformUsage {
  totalTranscriptionMinutes: number;
  totalExtractionCredits: number;
  totalStorageBytes: number;
  topOrganizations: OrganizationUsageSummary[];
  usageByTier: Record<string, Record<string, number>>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface PlatformSettings {
  registrationEnabled: boolean;
  registrationDisabledMessage?: string;
  platformName: string;
  maintenanceMode: boolean;
  maintenanceMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformSettingsUpdate {
  registrationEnabled?: boolean;
  registrationDisabledMessage?: string;
  platformName?: string;
  maintenanceMode?: boolean;
  maintenanceMessage?: string;
}

export interface AdminPlan {
  id: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceYearly: number;
  stripePriceIdMonthly?: string;
  stripePriceIdYearly?: string;
  stripeProductId?: string;
  trialDays: number;
  features: string[];
  limits: Record<string, number>;
  isActive: boolean;
  yearlySavingsPercent: number;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// API Methods
// =============================================================================

export const adminApi = {
  // ===========================================================================
  // Platform Statistics
  // ===========================================================================

  /**
   * Get platform-wide statistics
   */
  async getStats(): Promise<PlatformStats> {
    const response = await apiClient.get<any>('/admin/stats');
    return transformStats(response.data);
  },

  // ===========================================================================
  // User Management
  // ===========================================================================

  /**
   * List all users with filtering and pagination
   */
  async listUsers(params: AdminUserListParams = {}): Promise<PaginatedResponse<AdminUser>> {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.set('search', params.search);
    if (params.isActive !== undefined) queryParams.set('is_active', String(params.isActive));
    if (params.isSuperAdmin !== undefined) queryParams.set('is_super_admin', String(params.isSuperAdmin));
    if (params.organizationId) queryParams.set('organization_id', params.organizationId);
    if (params.page) queryParams.set('page', String(params.page));
    if (params.pageSize) queryParams.set('page_size', String(params.pageSize));

    const response = await apiClient.get<any>(`/admin/users?${queryParams.toString()}`);
    return {
      items: response.data.items.map(transformUser),
      total: response.data.total,
      page: response.data.page,
      pageSize: response.data.pageSize ?? response.data.page_size,
      hasMore: response.data.hasMore ?? response.data.has_more,
    };
  },

  /**
   * Get user details
   */
  async getUser(userId: string): Promise<AdminUser> {
    const response = await apiClient.get<any>(`/admin/users/${userId}`);
    return transformUser(response.data);
  },

  /**
   * Update user admin fields
   */
  async updateUser(userId: string, data: AdminUserUpdate): Promise<AdminUser> {
    const response = await apiClient.patch<any>(`/admin/users/${userId}`, {
      is_active: data.isActive,
      is_super_admin: data.isSuperAdmin,
      is_email_verified: data.isEmailVerified,
    });
    return transformUser(response.data);
  },

  /**
   * Delete user (soft delete)
   */
  async deleteUser(userId: string): Promise<void> {
    await apiClient.delete(`/admin/users/${userId}`);
  },

  // ===========================================================================
  // Organization Management
  // ===========================================================================

  /**
   * List all organizations with filtering and pagination
   */
  async listOrganizations(params: AdminOrgListParams = {}): Promise<PaginatedResponse<AdminOrganization>> {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.set('search', params.search);
    if (params.tier) queryParams.set('tier', params.tier);
    if (params.includeDeleted !== undefined) queryParams.set('include_deleted', String(params.includeDeleted));
    if (params.page) queryParams.set('page', String(params.page));
    if (params.pageSize) queryParams.set('page_size', String(params.pageSize));

    const response = await apiClient.get<any>(`/admin/organizations?${queryParams.toString()}`);
    return {
      items: response.data.items.map(transformOrganization),
      total: response.data.total,
      page: response.data.page,
      pageSize: response.data.pageSize ?? response.data.page_size,
      hasMore: response.data.hasMore ?? response.data.has_more,
    };
  },

  /**
   * Get organization details
   */
  async getOrganization(orgId: string): Promise<AdminOrganization> {
    const response = await apiClient.get<any>(`/admin/organizations/${orgId}`);
    return transformOrganization(response.data);
  },

  /**
   * Update organization admin fields
   */
  async updateOrganization(orgId: string, data: AdminOrganizationUpdate): Promise<AdminOrganization> {
    const response = await apiClient.patch<any>(`/admin/organizations/${orgId}`, {
      tier: data.tier,
      tier_expires_at: data.tierExpiresAt,
      is_deleted: data.isDeleted,
    });
    return transformOrganization(response.data);
  },

  /**
   * Delete organization (soft delete)
   */
  async deleteOrganization(orgId: string): Promise<void> {
    await apiClient.delete(`/admin/organizations/${orgId}`);
  },

  // ===========================================================================
  // Usage & Credits
  // ===========================================================================

  /**
   * Get platform-wide usage statistics
   */
  async getUsage(): Promise<PlatformUsage> {
    const response = await apiClient.get<any>('/admin/usage');
    return transformUsage(response.data);
  },

  // ===========================================================================
  // Platform Settings
  // ===========================================================================

  /**
   * Get platform settings
   */
  async getSettings(): Promise<PlatformSettings> {
    const response = await apiClient.get<any>('/admin/settings');
    return transformSettings(response.data);
  },

  /**
   * Update platform settings
   */
  async updateSettings(data: PlatformSettingsUpdate): Promise<PlatformSettings> {
    const response = await apiClient.patch<any>('/admin/settings', {
      registration_enabled: data.registrationEnabled,
      registration_disabled_message: data.registrationDisabledMessage,
      platform_name: data.platformName,
      maintenance_mode: data.maintenanceMode,
      maintenance_message: data.maintenanceMessage,
    });
    return transformSettings(response.data);
  },

  // ===========================================================================
  // Subscription Plans
  // ===========================================================================

  /**
   * List all subscription plans
   */
  async listPlans(includeInactive = false): Promise<AdminPlan[]> {
    const queryParams = new URLSearchParams();
    if (includeInactive) queryParams.set('include_inactive', 'true');

    const response = await apiClient.get<any>(`/admin/plans?${queryParams.toString()}`);
    return (response.data || []).map(transformPlan);
  },
};

// =============================================================================
// Transform Functions
// =============================================================================

// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformStats(data: any): PlatformStats {
  return {
    totalUsers: data.totalUsers ?? data.total_users ?? 0,
    activeUsers30d: data.activeUsers30d ?? data.active_users_30d ?? 0,
    totalOrganizations: data.totalOrganizations ?? data.total_organizations ?? 0,
    activeOrganizations: data.activeOrganizations ?? data.active_organizations ?? 0,
    totalProjects: data.totalProjects ?? data.total_projects ?? 0,
    totalTasks: data.totalTasks ?? data.total_tasks ?? 0,
    totalStorageBytes: data.totalStorageBytes ?? data.total_storage_bytes ?? 0,
    organizationsByTier: data.organizationsByTier ?? data.organizations_by_tier ?? {},
  };
}

function transformUser(data: any): AdminUser {
  return {
    id: data.id,
    email: data.email,
    displayName: data.displayName ?? data.display_name,
    avatarUrl: data.avatarUrl ?? data.avatar_url,
    locale: data.locale,
    theme: data.theme,
    timezone: data.timezone,
    isEmailVerified: data.isEmailVerified ?? data.is_email_verified,
    isActive: data.isActive ?? data.is_active,
    isSuperAdmin: data.isSuperAdmin ?? data.is_super_admin,
    lastLoginAt: data.lastLoginAt ?? data.last_login_at,
    lastActiveAt: data.lastActiveAt ?? data.last_active_at,
    failedLoginAttempts: data.failedLoginAttempts ?? data.failed_login_attempts,
    lockedUntil: data.lockedUntil ?? data.locked_until,
    organizations: (data.organizations || []).map((org: any) => ({
      organizationId: org.organizationId ?? org.organization_id,
      organizationName: org.organizationName ?? org.organization_name,
      role: org.role,
      joinedAt: org.joinedAt ?? org.joined_at,
    })),
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformOrganization(data: any): AdminOrganization {
  return {
    id: data.id,
    name: data.name,
    slug: data.slug,
    description: data.description,
    logoUrl: data.logoUrl ?? data.logo_url,
    tier: data.tier,
    effectiveTier: data.effectiveTier ?? data.effective_tier ?? data.tier,
    tierExpiresAt: data.tierExpiresAt ?? data.tier_expires_at,
    stripeCustomerId: data.stripeCustomerId ?? data.stripe_customer_id,
    stripeSubscriptionId: data.stripeSubscriptionId ?? data.stripe_subscription_id,
    subscriptionStatus: data.subscriptionStatus ?? data.subscription_status,
    subscriptionPlanName: data.subscriptionPlanName ?? data.subscription_plan_name,
    memberCount: data.memberCount ?? data.member_count,
    projectCount: data.projectCount ?? data.project_count,
    storageUsedBytes: data.storageUsedBytes ?? data.storage_used_bytes,
    isDeleted: data.isDeleted ?? data.is_deleted,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformUsage(data: any): PlatformUsage {
  return {
    totalTranscriptionMinutes: data.totalTranscriptionMinutes ?? data.total_transcription_minutes,
    totalExtractionCredits: data.totalExtractionCredits ?? data.total_extraction_credits,
    totalStorageBytes: data.totalStorageBytes ?? data.total_storage_bytes,
    topOrganizations: (data.topOrganizations ?? data.top_organizations ?? []).map((org: any) => ({
      organizationId: org.organizationId ?? org.organization_id,
      organizationName: org.organizationName ?? org.organization_name,
      tier: org.tier,
      transcriptionMinutesUsed: org.transcriptionMinutesUsed ?? org.transcription_minutes_used,
      transcriptionMinutesLimit: org.transcriptionMinutesLimit ?? org.transcription_minutes_limit,
      extractionCreditsUsed: org.extractionCreditsUsed ?? org.extraction_credits_used,
      extractionCreditsLimit: org.extractionCreditsLimit ?? org.extraction_credits_limit,
      storageUsedBytes: org.storageUsedBytes ?? org.storage_used_bytes,
      storageLimitBytes: org.storageLimitBytes ?? org.storage_limit_bytes,
    })),
    usageByTier: data.usageByTier ?? data.usage_by_tier,
  };
}

function transformSettings(data: any): PlatformSettings {
  return {
    registrationEnabled: data.registrationEnabled ?? data.registration_enabled,
    registrationDisabledMessage: data.registrationDisabledMessage ?? data.registration_disabled_message,
    platformName: data.platformName ?? data.platform_name,
    maintenanceMode: data.maintenanceMode ?? data.maintenance_mode,
    maintenanceMessage: data.maintenanceMessage ?? data.maintenance_message,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformPlan(data: any): AdminPlan {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    priceMonthly: data.priceMonthly ?? data.price_monthly,
    priceYearly: data.priceYearly ?? data.price_yearly,
    stripePriceIdMonthly: data.stripePriceIdMonthly ?? data.stripe_price_id_monthly,
    stripePriceIdYearly: data.stripePriceIdYearly ?? data.stripe_price_id_yearly,
    stripeProductId: data.stripeProductId ?? data.stripe_product_id,
    trialDays: data.trialDays ?? data.trial_days,
    features: data.features || [],
    limits: data.limits || {},
    isActive: data.isActive ?? data.is_active,
    yearlySavingsPercent: data.yearlySavingsPercent ?? data.yearly_savings_percent,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

export default adminApi;
